/**
 * redirectResultOnce — ONE `getRedirectResult(auth)` per page load, shared.
 *
 * Firebase only memoizes the redirect outcome AFTER the first call settles
 * (`redirectOutcomeMap`); calls that OVERLAP are not deduped. Each one spins up
 * its own popup/redirect operation, and they race on the read-then-clear of the
 * persisted pending-redirect status. Two things fall out of that race:
 *
 *   1. The loser gets `null` even when a credential was there, so a
 *      `if (cred) …` branch silently never runs.
 *   2. The already-settled operation can still receive an auth event, and the
 *      SDK's `debugAssert` fires — `INTERNAL ASSERTION FAILED: Pending promise
 *      was never set` (`AbstractPopupRedirectOperation.reject`). Harmless to the
 *      session (that lives on `onAuthStateChanged`), but it lands in `crashLogs`
 *      as an unhandledrejection. Note @firebase/auth does NOT strip its
 *      `debugAssert` calls from the shipped browser bundle — unlike Firestore,
 *      there is no debug/release split, so this reaches production users.
 *
 * Overlap is reachable because two independent mount effects call this: the
 * `useAuth` hook (mounted twice over a boot — once by `GymSelector` at "/",
 * again by `useMainApp` once the PWA restore navigates to `/gym/<slug>` while
 * the first call is still on the network) and `GlobalProfile` on `/profile`.
 *
 * Callers attach their OWN `.then` / `.catch` to the shared promise, so every
 * one of them sees the same outcome — including the rejection.
 *
 * No reset: a new `signInWithRedirect` / `linkWithRedirect` always leaves the
 * page, so the next redirect outcome arrives with a fresh module instance.
 */
import { getRedirectResult } from "firebase/auth";
import { auth } from "../firebase";

let pending = null;

export function redirectResultOnce() {
  if (!pending) {
    pending = getRedirectResult(auth);
    // Keep the SHARED promise handled even if a consumer attaches late (a
    // lazy-loaded route mounting after the rejection would otherwise raise a
    // second unhandledrejection — the exact noise this module exists to stop).
    // Consumers still see the rejection through their own derived chains.
    pending.catch(() => {});
  }
  return pending;
}
